import {
	BaseExpressionTree,
	ClassDeclarationTree,
	CompilationUnitTree,
	EnumDeclarationTree,
	FunctionDeclarationTree,
	ImportDeclarationTree,
	InterfaceDeclarationTree,
	LocalVariableDeclarationListTree,
	LocalVariableDeclarationTree,
	modifiersToAccessorFlags,
	NameTree,
	StaticInitializationBlockTree,
} from "./model";
import {Parser} from "./parser";
import {
	AccessorFlags,
	ClassDeclarationInfo,
	GeneralType,
	GeneralTypeOrAny,
	hasFlag,
	InterfaceDeclarationInfo,
	LocalVariableDef,
	maybeUnparameterize,
	MemberFieldInfo,
	MethodDeclarationInfo,
	NonGenericType,
	PackageInfo,
	ParameterizedType,
	PermittedSuperclassType,
	RawType,
	TypeParameterInfo,
} from "./type_model";
import {cafebabeAssert, CompileError, JavaVersion} from "./error";
import {
	addImportOnDemand,
	anyType,
	classCircularityCheck,
	extractTypeParameters,
	getOrCreatePackage,
	instantiateArray,
	instantiateNonGenericType,
	instantiateType,
	lookupBinaryName,
	resolveBlockTree,
	resolveExpressionType,
	resolveLocalVariableDeclarationListTree,
	resolvePackageOrTypeName,
	resolveTypeName,
	resolveTypeParameters,
	resolveTypeTree,
	ScopeChainNode,
	splitBinaryName,
	tryAddNamedImport,
	walkAllDefinedClasses,
	walkAllPackages,
	anyWildcardError,
	putativelyFinalFieldActuallyChanges,
	makeBlockScope,
	registerClass,
	validateThrowsClause,
	AnalysisCtx,
} from "./analysis";
import {PerfCounters} from "./perf_counters";

export type JdkInfo = {
	[binaryName: string]: ClassDeclarationInfo | InterfaceDeclarationInfo;
};

export type ProjectOptions = {
	// Pass the cyclically deserialized jdk8-info.json, loaded using the preferred method
	jdk: JdkInfo;

	payload?: Record<string, string>;

	// Emit a compile error if the package declaration on a CU does not correspond to the file path
	enforcePackageBoundaries?: boolean;

	// Mark all classes as built-in
	generateJdk?: boolean;

	// Whether to not load classes from AopsTheater
	omitTheaterClasses?: boolean;

	// Whether to enable performance counters
	enablePerfCounters?: boolean;

	// Java version
	javaVersion?: JavaVersion;
};

type FileInfo = {
	name: string;
	contents: string;

	parser?: Parser;
	cu?: CompilationUnitTree;

	/**
	 * Classes/interfaces/constants from these files were used during type resolution on this file; if these files
	 * change, then this file needs to be re-analysed.
	 *
	 * undefined if the list of file dependencies is not yet known.
	 */
	fileDependencies?: Set<string>;

	typeErrors: CompileError[];
};

export function fillContext(
	ctx: Partial<AnalysisCtx>,
	javaVersion: JavaVersion
): AnalysisCtx {
	return {
		reportError: (err: CompileError) => {},
		javaVersion,
		...ctx,
	};
}

/**
 * A "project" maintains a list of parsed files and accepts updates to file contents, then reports completions,
 * compile errors, etc.
 */
export class Project {
	// Map of fully qualified file names from the root directory (e.g. "StudentCode/Example.java") to file contents.
	public files: Map<string, FileInfo> = new Map();

	private readonly _perf: PerfCounters | null = null;

	// Highest level scope chain node: i.e., everything in java.lang.*
	public rootScope: ScopeChainNode = {
		scopeType: "root",
		tree: null,
		types: new Map(),
		typeParameters: new Map(),
		locals: new Map(),
		memberDeclarations: new Map(),
		memberFields: new Map(),
		packages: new Map(),
		prev: null,
	};

	// List of changed files since last type analysis
	public changedSinceTypeAnalysis: Set<string> = new Set();

	constructor(readonly options: ProjectOptions) {
		if (options.payload) {
			Object.entries(options.payload).forEach(([name, contents]) => {
				this.updateFile(name, contents);
			});
		}

		if (options.enablePerfCounters) {
			this._perf = new PerfCounters();
		}

		this._perf?.start("read-builtin");
		this.readBuiltinClasses(options.jdk);
		this._perf?.end("read-builtin");
	}

	get perf() {
		return this._perf ?? new PerfCounters();
	}

	javaVersion() {
		return this.options.javaVersion ?? 8;
	}

	private _generatingJdk(): boolean {
		return !!this.options.generateJdk;
	}

	readBuiltinClasses(infos: JdkInfo) {
		if (this._generatingJdk()) {
			// skip this step we're bootstrapping JDK 8!
			return;
		}

		for (const [binaryName, info] of Object.entries(infos)) {
			const {pkg, clazz} = splitBinaryName(binaryName);
			if (clazz.length > 1) {
				continue; // inner classes will be dealt with during de-serialisation
			}

			if (this.options.omitTheaterClasses && this.isTheaterPackage(pkg)) {
				continue;
			}

			const p = getOrCreatePackage(
				this.rootScope,
				new Parser(pkg.join(".")).consumeQualifiedIdentifier()
			);
			cafebabeAssert(p !== null, "Could not create package for builtin type");
			p.builtin ||= this._generatingJdk();
			p.topLevelClasses.set(clazz[0]!, info);
			info.package = p;
		}
	}

	private isTheaterPackage(pkg: string[]) {
		return !!(pkg[0] && (pkg[0] === "AopsTheater" || pkg[0] === "JsonSimple"));
	}

	/**
	 * Return the package declaration expected for the given file. e.g., StudentCode/Chicken.java -> "StudentCode"
	 */
	static expectedPackage(filename: string) {
		const slash = filename.lastIndexOf("/");
		if (slash === -1) return "";
		return filename.slice(0, slash).replaceAll("/", ".");
	}

	/**
	 * If package checks are enabled, check that the file has a package declaration corresponding to its path.
	 * @returns True if there was an error.
	 */
	private _packageDeclarationCheck(
		filename: string,
		cu: CompilationUnitTree,
		parser: Parser
	): boolean {
		if (this.options.enforcePackageBoundaries) {
			const expectedPackage = Project.expectedPackage(filename);

			if (cu.getPackageUnchecked() !== expectedPackage) {
				const span = cu.packageDeclaration?.span ?? {start: 0, end: 1};
				const message = expectedPackage
					? `Expected package declaration ${expectedPackage}`
					: "Root-level file should have no package declaration";
				parser.emitError(message, span);
				return true;
			}
		}

		return false;
	}

	/**
	 * Re-parse all compilation units that have changed.
	 */
	parseCompilationUnits() {
		// Re-parse all CUs
		for (const [name, file] of this.files) {
			if (!file.contents || file.cu) continue;

			this._perf?.start("lex");
			const parser = (file.parser = new Parser(file.contents));
			this._perf?.end("lex");

			this._perf?.start("parse");
			const cu = parser.consumeCompilationUnit();
			this._perf?.end("parse");
			this._perf?.start("spans");
			cu.resolveSpans();
			this._perf?.end("spans");

			this.changedSinceTypeAnalysis.add(name);
			// like the backend, don't emit a CU to avoid polluting types if the package declaration is wrong
			if (this._packageDeclarationCheck(name, cu, parser)) continue;

			file.cu = cu;
		}
	}

	/**
	 * Get the compilation unit associated with the given file name.
	 */
	getCU(filename: string): CompilationUnitTree | null {
		this.parseCompilationUnits();

		return this.files.get(filename)?.cu ?? null;
	}

	/**
	 * Walk the package hierarchy and delete all empty packages.
	 */
	deleteEmptyPackages() {
		let changed;
		do {
			changed = false;
			walkAllPackages(this.rootScope, {excludeBuiltin: true}, (pkg) => {
				const {topLevelClasses, name, subpackages, parent} = pkg;
				if (topLevelClasses.size === 0 && subpackages.size === 0) {
					if (parent === null) {
						cafebabeAssert(
							this.rootScope.packages!.delete(name),
							"deleteEmptyPackages inconsistency"
						);
					} else {
						cafebabeAssert(
							parent.subpackages.delete(name),
							"deleteEmptyPackages inconsistency"
						);
					}
					changed = true;
				}
				return true;
			});
		} while (changed);
	}

	filesNeedingReanalysis(): Set<string> {
		// Flood fill files to find which files need to be re-analysed
		const needsReanalysis = new Set<string>(this.changedSinceTypeAnalysis);
		let changed;
		do {
			changed = false;
			for (const [name, f] of this.files) {
				const deps = f.fileDependencies;
				if (!deps || needsReanalysis.has(name)) continue;
				for (const dep of deps) {
					if (needsReanalysis.has(dep)) {
						needsReanalysis.add(name);
						changed = true;
					}
				}
			}
		} while (changed);
		return needsReanalysis;
	}

	clearOldClassesDefinedInFiles(files: Set<string>) {
		function clearMap(
			m: PackageInfo["topLevelClasses"],
			pkg: PackageInfo | null
		) {
			for (const [key, entry] of m) {
				if (files.has(entry.file)) {
					m.delete(key);
					pkg?.scope?.types?.delete(key);
				}
			}
		}

		// Remove all classes defined in files selected for re-analysis
		walkAllPackages(this.rootScope, {excludeBuiltin: true}, (pkg) => {
			clearMap(pkg.topLevelClasses, pkg);
			return true;
		});
		// Remove top-level classes not in a package
		clearMap(this.rootScope.types!, null);
		this.deleteEmptyPackages();
	}

	/**
	 * Remove files from the analysis set which have no compilation unit associated with them.
	 */
	removeEmptyFilesFromAnalysisSet(filenames: Set<string>) {
		for (const cf of filenames) {
			const file = this.files.get(cf);
			if (!file?.cu) {
				// no compilation unit associated with this file
				filenames.delete(cf);
			} else {
				file.typeErrors.length = 0; // clear type errors from last time
			}
		}
	}

	getFile(filename: string) {
		return this.files.get(filename) ?? null;
	}

	private _getParsedFile(
		filename: string
	): FileInfo & {cu: CompilationUnitTree} {
		const file = this.files.get(filename);
		cafebabeAssert(file !== undefined, `File ${filename} not found`);
		cafebabeAssert(file.cu !== undefined, `File ${filename} not yet parsed`);
		return file as FileInfo & {cu: CompilationUnitTree};
	}

	collectTopLevelAndMemberClasses(
		files: Set<string>
	): (EnumDeclarationTree | ClassDeclarationTree | InterfaceDeclarationTree)[] {
		// Class declarations which, at the very least, have a name and so may be analysed. Only includes top-level
		// and member classes, not local and anonymous classes.
		const saneDeclarations: (
			| EnumDeclarationTree
			| ClassDeclarationTree
			| InterfaceDeclarationTree
		)[] = [];

		for (const cf of files) {
			const parsed = this._getParsedFile(cf);
			const cu = parsed.cu;

			// Create package scope and attach CU scope to the package
			const package_ = getOrCreatePackage(
				this.rootScope,
				cu.packageDeclaration?.name ?? null
			);
			if (package_) package_.builtin ||= this._generatingJdk();
			const packageScope = package_
				? (package_.scope ??= {
						scopeType: "package",
						tree: null,
						types: new Map(),
						prev: this.rootScope,
						info: package_,
					})
				: this.rootScope;

			if (cu.packageDeclaration) {
				cu.packageDeclaration.referent = package_;
			}

			const cuScope: ScopeChainNode = {
				scopeType: "cu",
				tree: cu,
				types: new Map(),
				prev: packageScope,
			};

			cu.setScope(cuScope);
			if (package_?.fullyQualifiedName !== "java.lang") {
				const java_lang =
					this.getClassByBinaryName("java.lang.Object")!.package!;
				java_lang.topLevelClasses.forEach((clazz, name) => {
					if (hasFlag(clazz, AccessorFlags.public)) {
						addImportOnDemand(cuScope, clazz);
					}
				});
			}

			for (const tree of cu.typeDeclarations) {
				if (tree.isTypeDeclaration()) {
					const result = registerClass(
						cf,
						tree,
						package_,
						cuScope,
						package_,
						package_?.topLevelClasses ?? this.rootScope.types!,
						true,
						saneDeclarations,
						this.makeAnalysisContext(cf)
					);

					if (result) {
						const info = result.topLevelInfo;
						packageScope.types!.set(info.simpleName, info);
					}
				}
			}
		}

		return saneDeclarations;
	}

	resolveCUTypeImportsAndInheritanceHierarchy(
		decls: (
			| EnumDeclarationTree
			| ClassDeclarationTree
			| InterfaceDeclarationTree
		)[],
		files: Set<string>
	) {
		// We have the following rules for resolving types-in-scope at the highest levels.
		//
		// import package.*;                -> Add all public top-level classes from the package to the scope
		// import package.Class;            -> Add Class to the scope if it is public
		// import static package.Class.*;   -> Add all static classes of the class, including all superclasses' static classes, to the scope
		// import package.Class.*;

		// For each CU, imports whose referent is not yet resolved
		const importErrors: Map<
			ImportDeclarationTree,
			{file: string; error: CompileError}[]
		> = new Map();

		for (const cf of files) {
			const cu = this._getParsedFile(cf).cu;
			const cuScope = cu.scope!;
			cafebabeAssert(
				cuScope !== null,
				"resolveCUTypeImports: CU scope should exist"
			);
			const package_ = cu.getPackage();

			cu.imports.forEach((import_) => {
				const pushError = (err: CompileError) => {
					const arr = importErrors.get(import_);
					const tagged = {file: cf, error: err};
					if (arr) arr.push(tagged);
					else importErrors.set(import_, [tagged]);
				};

				const ctx: AnalysisCtx = {
					reportError: pushError,
					javaVersion: this.options.javaVersion ?? 8,
				};

				if (import_.isStarImport) {
					const importLhs = import_.name as NameTree;
					cafebabeAssert(
						importLhs instanceof NameTree,
						"Import declaration qualified identifier should have a dummy ident inserted"
					);
					const resolved = resolvePackageOrTypeName(
						cuScope.prev! /* use package scope for import resolution */,
						importLhs.qualifier,
						package_,
						"", // file name guaranteed unequal -- disallow importing private types within the same file
						ctx,
						false
					);
					cafebabeAssert(
						resolved?.type !== "tparam",
						"No type parameters in CU scope"
					);
					const ref = (import_.referent = resolved);

					if (!ref) return;

					// Add all types from import-on-demand declaration
					if (import_.isStatic && ref.type === "package") {
						this.reportTypeError(
							cf,
							new CompileError(
								"Static modifier not allowed when importing a package",
								import_.staticToken!.span
							)
						);
						return;
					}

					(ref.type === "package"
						? ref.topLevelClasses
						: [...ref.memberClasses.values()]
					).forEach((topLevel) => {
						if (
							hasFlag(topLevel, AccessorFlags.public) &&
							import_.isStatic === hasFlag(topLevel, AccessorFlags.static)
						) {
							addImportOnDemand(cuScope, topLevel);
						}
					});
				} else {
					const ref = (import_.referent = resolveTypeName(
						cuScope.prev!,
						import_.name,
						package_,
						"" /* as above */,
						ctx
					));
					if (!ref) return;
					const error = tryAddNamedImport(cuScope, ref);
					if (error)
						this.reportTypeError(cf, new CompileError(error, import_.span));
				}
			});
		}

		// Report all errors due to imports
		for (const errList of importErrors.values()) {
			errList.forEach(({file, error}) => {
				this.reportTypeError(file, error);
			});
		}

		// For each class, resolve first the type parameters, then the extends clause, then the implements clause,
		// then create the class scope
		decls.forEach(this.resolveDeclarationTypeRelations.bind(this));
		decls.forEach(this.resolveDeclarationMethodsAndFields.bind(this));
	}

	resolveEnumConstants(decl: EnumDeclarationTree) {
		if (!decl.body || !decl.constants) return;

		const referent = decl.referent!;
		const scope = decl.scope;
		cafebabeAssert(referent !== null, "Referent have been created by now");
		cafebabeAssert(scope !== null, "Scope have been created by now");

		for (const constant of decl.constants) {
			const member: MemberFieldInfo = {
				type: "member",
				file: referent.file,
				ty: instantiateType(referent),
				flags: AccessorFlags.public | AccessorFlags.static,
				builtin: referent.builtin,
				name: constant.name.value,
				memberOf: referent,
				doc: constant.docComment?.value,
			};
			scope.memberFields ??= new Map();
			const existing = scope.memberFields.get(constant.name.value);
			if (existing) {
				continue; // TODO report error
			} else {
				scope.memberFields.set(
					constant.name.value,
					(constant.referent = member)
				);
				referent.members.set(constant.name.value, member);
			}
		}
	}

	resolveDeclarationMethodsAndFields(
		decl: EnumDeclarationTree | ClassDeclarationTree | InterfaceDeclarationTree
	) {
		if (!decl.body) return;
		const referent = decl.referent!;
		const scope = decl.scope;
		cafebabeAssert(referent !== null, "Referent have been created by now");
		cafebabeAssert(scope !== null, "Scope have been created by now");

		const ctx = this.makeAnalysisContext(referent.file);
		if (decl instanceof EnumDeclarationTree) {
			this.resolveEnumConstants(decl);
		}

		for (const elem of decl.body) {
			if (elem instanceof FunctionDeclarationTree) {
				let modifiers = modifiersToAccessorFlags(elem.modifiers); // TODO cure modifiers
				if (decl instanceof InterfaceDeclarationTree)
					modifiers |= AccessorFlags.static;

				const fnScope: ScopeChainNode = {
					scopeType: "function",
					tree: elem,
					prev: scope,
					isStatic: (modifiers & AccessorFlags.static) !== 0,
				};

				elem.setScope(fnScope);

				if (elem.body) {
					const fnBlockScope = makeBlockScope(fnScope);
					elem.body?.setScope(fnBlockScope);
				}

				let typeParameters: TypeParameterInfo[] | null = null;
				if (elem.typeParams) {
					typeParameters = extractTypeParameters(
						referent,
						elem.typeParams,
						ctx
					);
					fnScope.typeParameters = new Map([
						...typeParameters.map(
							(param) => [param.name, param] as [string, TypeParameterInfo]
						),
					]);
					resolveTypeParameters(fnScope, elem.typeParams, ctx);
				}

				const argsTree = elem.args;
				const isCtor = elem.isCtor;
				if (isCtor) {
					if (decl instanceof InterfaceDeclarationTree) {
						ctx.reportError(
							new CompileError("Interfaces cannot have constructors", elem.span)
						);
						continue;
					}
					if (elem.name.value !== decl.name!.identifier) {
						ctx.reportError(
							new CompileError(
								"Constructor name must match class name",
								elem.name.span
							)
						);
						continue;
					}
				}

				const returns = isCtor
					? instantiateType(referent, new Map())
					: elem.returnType
						? resolveTypeTree(scope, elem.returnType, ctx)
						: {type: "any"};
				const methodInfo = {};
				const args: LocalVariableDef[] =
					argsTree?.args.map((arg) => {
						let ty = arg.type
							? (resolveTypeTree(fnScope, arg.type, ctx) as GeneralTypeOrAny)
							: anyType();
						const variableArity = arg.ellipsis !== null;
						if (variableArity && ty) {
							ty = instantiateArray(ty);
						}
						return {
							type: "local",
							name: arg.name?.identifier ?? "",
							def: arg,
							ty,
							parent: argsTree!,
							memberOf: methodInfo as any /* to induce circular reference */,
							variableArity,
						} as LocalVariableDef;
					}) ?? [];

				const locals: Map<string, LocalVariableDef> = (fnScope.locals =
					new Map());
				for (const arg of args) {
					locals.set(arg.name, arg);
				}

				const throws = validateThrowsClause(
					scope,
					elem.args?.throwsClause ?? null,
					ctx
				);
				const info = Object.assign(methodInfo, {
					type: "function",
					file: referent.file,
					flags: modifiers,
					builtin: referent.builtin,
					typeParameters,
					returns,
					arguments: args.map((arg) => arg.ty),
					names: args.map((arg) => arg.name),
					name: elem.name.value,
					memberOf: referent,
					innerClasses: [], // TODO what to do here.... :(
					isCtor,
					doc: elem.docComment?.value,
					isVariableArity: args.at(-1)?.variableArity ?? false,
					tree: elem,
					throws,
				} as MethodDeclarationInfo);
				elem.referent = info;

				scope.memberDeclarations ??= new Map();
				const fnName = elem.name.value;
				{
					const existing = referent.functions.get(fnName);
					if (!existing) {
						referent.functions.set(fnName, [info]);
					} else {
						existing.push(info);
					}
				}
				const existing = scope.memberDeclarations.get(elem.name.value);
				if (existing)
					existing.push(info); // TODO check for methods with same args
				else scope.memberDeclarations.set(elem.name.value, [info]);
			} else if (elem instanceof LocalVariableDeclarationListTree) {
				const modifiers = modifiersToAccessorFlags(elem.modifiers);
				const defs = resolveLocalVariableDeclarationListTree(
					scope,
					elem,
					ctx,
					(modifiers & AccessorFlags.final) !== 0
				);

				const members: Map<string, MemberFieldInfo> = (scope.memberFields ??=
					new Map());
				for (const def of defs) {
					if (members.get(def.name)) {
						ctx.reportError(
							new CompileError(
								"Duplicate field name",
								(def.def as LocalVariableDeclarationTree).name!.span
							)
						);
					} else {
						const constantValue = putativelyFinalFieldActuallyChanges(
							referent,
							def.name
						)
							? null
							: def.constantValue;
						const memberFieldInfo: MemberFieldInfo = {
							type: "member",
							flags: modifiers,
							file: referent.file,
							builtin: referent.builtin,
							name: def.name,
							memberOf: referent,
							ty: def.ty,
							constantValue,
						};
						members!.set(def.name, memberFieldInfo);
						referent.members.set(def.name, memberFieldInfo);
					}
				}
			}
		}
	}

	resolveDeclarationTypeRelations(
		decl: EnumDeclarationTree | ClassDeclarationTree | InterfaceDeclarationTree
	) {
		const referent = decl.referent!;
		const scope =
			decl.scope
				?.prev; /* use scope ENCLOSING the class scope for extends/implements */

		cafebabeAssert(referent !== null, "Declaration should have an info now");
		cafebabeAssert(scope != null, "Declaration should have a scope now");

		const tyParams: Map<string, TypeParameterInfo> = new Map();
		decl.scope!.typeParameters = tyParams;
		scope.typeParameters ??= new Map();

		for (const param of referent.typeParameters) {
			cafebabeAssert(
				!tyParams.has(param.name),
				"Duplicate type parameters should have already been excluded"
			);
			if (param.parameterOf === referent) {
				// Only add type parameters which are a part of this member class; the other type parameters
				// will have already been added to the scope of the outer class
				tyParams!.set(param.name, param);
			}
		}

		referent.tree = decl;
		if (!(decl instanceof EnumDeclarationTree)) {
			decl = decl as ClassDeclarationTree | InterfaceDeclarationTree;
			const ctx = this.makeAnalysisContext(referent.file);

			// Temporarily add type arguments to the parent scope so that they may be used when resolving the extends
			// and implements clauses
			const oldTypeParameters = scope.typeParameters;
			scope.typeParameters = new Map([...scope.typeParameters, ...tyParams]);

			resolveTypeParameters(scope, decl.typeargs, ctx);
			if (decl instanceof ClassDeclarationTree) {
				this.resolveClassDeclarationRelations(decl, ctx);

				if (
					referent.extends === null &&
					referent.binaryName !== "java.lang.Object"
				) {
					// All classes besides java.lang.Object extend the primordial java.lang.Object
					const java_lang_Object =
						this.getClassByBinaryName("java.lang.Object");
					cafebabeAssert(
						java_lang_Object !== null,
						"Primordial object must exist"
					);
					referent.extends = instantiateNonGenericType(java_lang_Object);
				}
			} else {
				this.resolveInterfaceDeclarationRelations(decl);
			}

			scope.typeParameters = oldTypeParameters;
		} else {
			// EnumDeclarationTree
			// "The direct superclass of an enum type E is Enum<E> (§8.1.4)."
			const java_lang_Enum = this.getClassByBinaryName("java.lang.Enum");
			cafebabeAssert(
				java_lang_Enum !== null,
				"java.lang.Enum should be defined"
			);
			referent.extends = instantiateType(
				java_lang_Enum,
				new Map([
					[
						java_lang_Enum.typeParameters[0]!,
						instantiateNonGenericType(referent),
					],
				])
			);
		}
	}

	/**
	 * Update the registry of non-builtin types with class and interface names, function declarations, etc., but do
	 * not perform type analysis or checking within function declarations.
	 *
	 * Also, updates the highest level scope chain node for each compilation unit to contain the appropriate imports,
	 * but does not compute scope information for anything deeper – which must be done during per-CU type analysis.
	 *
	 * ALGORITHM
	 *
	 * - Parse all compilation units.
	 * - Look for changed compilation units.
	 *   - Remove types defined in CUs which have changed, or which depend on CUs that have changed. Mark all as changed.
	 * - Re-parse all changed compilation units.
	 * - For each compilation unit:
	 *   - Add packages
	 */
	analyze() {
		this.parseCompilationUnits();

		const needsReanalysis = this.filesNeedingReanalysis();
		this.changedSinceTypeAnalysis.clear();

		this._perf?.start("clear-old");
		this.clearOldClassesDefinedInFiles(needsReanalysis);
		this.removeEmptyFilesFromAnalysisSet(needsReanalysis);
		this._perf?.end("clear-old");

		this._perf?.start("collect-top");
		const declarationsToProcess =
			this.collectTopLevelAndMemberClasses(needsReanalysis);
		this.resolveCUTypeImportsAndInheritanceHierarchy(
			declarationsToProcess,
			needsReanalysis
		);
		this._perf?.end("collect-top");

		if (this._generatingJdk()) return; // all that is needed is top-level stuff -- rejoice

		this._perf?.start("resolve-bodies");
		declarationsToProcess.forEach(this.resolveDeclarationContents.bind(this));
		this._perf?.end("resolve-bodies");
	}

	resolveDeclarationContents(
		decl: ClassDeclarationTree | EnumDeclarationTree | InterfaceDeclarationTree
	) {
		const scope = decl.scope!;
		const ctx = this.makeAnalysisContext(decl.referent?.file!);
		decl.body?.forEach((body) => {
			if (body instanceof FunctionDeclarationTree) {
				resolveBlockTree(body.scope!, body.body, ctx);
			} else if (body instanceof StaticInitializationBlockTree) {
				const blockScope: ScopeChainNode = {
					scopeType: "block",
					isStatic: true,
					prev: scope,
				};
				body.setScope(blockScope);
				resolveBlockTree(blockScope, body.block, ctx);
			}
		});
		return decl;
	}

	/**
	 * Set the contents of the file.
	 */
	updateFile(filename: string, contents: string) {
		let existing = this.files.get(filename);
		if (existing?.contents === contents) return;

		if (typeof existing === "undefined") {
			existing = {name: filename, contents, typeErrors: []};
			this.files.set(filename, existing);
		}

		existing.contents = contents;
		existing.parser = undefined;
		existing.cu = undefined;

		this.changedSinceTypeAnalysis.add(filename);
	}

	/**
	 * Delete a file.
	 * @return True if the file existed.
	 */
	deleteFile(filename: string) {
		if (this.files.delete(filename)) {
			this.changedSinceTypeAnalysis.add(filename);
			return true;
		}
		return false;
	}

	makeAnalysisContext(file: string): AnalysisCtx {
		return {
			reportError: (err: CompileError) => {
				this.reportTypeError(file, err);
			},
			javaVersion: this.options.javaVersion ?? 8,
		};
	}

	private reportTypeError(file: string, error: CompileError) {
		const f = this.files.get(file);
		if (!f) return;
		f.typeErrors.push(error);
	}

	listClasses(
		excludeBuiltin = true
	): Set<ClassDeclarationInfo | InterfaceDeclarationInfo> {
		const classes = new Set<ClassDeclarationInfo | InterfaceDeclarationInfo>();
		walkAllDefinedClasses(this.rootScope, {excludeBuiltin}, (clazz) => {
			if (!excludeBuiltin || !clazz.builtin) classes.add(clazz);
			return true;
		});
		return classes;
	}

	listBinaryNames(): Set<string> {
		return new Set([...this.listClasses()].map((clazz) => clazz.binaryName));
	}

	/**
	 * e.g. "java.util.ArrayList" or "java.util.Map$Entry"
	 */
	getClassByBinaryName(binaryName: string) {
		return lookupBinaryName(this.rootScope, binaryName);
	}

	private resolveClassDeclarationRelations(
		decl: ClassDeclarationTree,
		ctx: AnalysisCtx
	) {
		const referent = decl.referent!;
		const scope = decl.scope!.prev!;

		if (decl.extendsClause) {
			// https://docs.oracle.com/javase/specs/jls/se8/html/jls-8.html#jls-8.1.4
			let resolution = resolveTypeTree(scope, decl.extendsClause, ctx);

			// "The ClassType must name an accessible class type (§6.6), or a compile-time error occurs."
			if (!resolution) return;

			// "The extends clause must not appear in the definition of the class Object, or a compile-time error occurs"
			cafebabeAssert(
				referent.binaryName !== "java.lang.Object",
				"primordial Object may not have a superclass"
			);

			const unparameterized = maybeUnparameterize(resolution);
			// Class: "The ClassType must name an accessible class type (§6.6), or a compile-time error occurs."
			if (unparameterized?.type !== "class")
				return reportError(
					new CompileError(
						`Superclass must be a class`,
						decl.extendsClause.span
					)
				);

			// "It is a compile-time error if the ClassType names a class that is final, because final classes are not
			// allowed to have subclasses"
			if (hasFlag(unparameterized, AccessorFlags.final))
				return reportError(
					new CompileError(
						`Attempting to extend final class ${unparameterized.simpleName}`,
						decl.extendsClause.span
					)
				);

			// "It is a compile-time error if the ClassType names the class Enum or any invocation of Enum"
			if (unparameterized.binaryName === "java.lang.Enum")
				return reportError(
					new CompileError(
						"May not extend java.lang.Enum (use an enum declaration)",
						decl.extendsClause.span
					)
				);

			resolution = resolution as NonGenericType | RawType | ParameterizedType;

			// "If the ClassType has type arguments, it must denote a well-formed parameterized type (§4.5), and none
			// of the type arguments may be wildcard type arguments, or a compile-time error occurs.
			if (anyWildcardError(resolution))
				return reportError(
					new CompileError(
						"Wildcards not allowed here",
						decl.extendsClause.span
					)
				);

			// "It is a compile-time error if a class depends on itself."
			const circularityCheck = classCircularityCheck(referent, resolution);
			if (circularityCheck)
				return reportError(
					new CompileError(circularityCheck, decl.extendsClause.span)
				);

			referent.extends = resolution;
		}

		if (decl.implementsClause) {
			// https://docs.oracle.com/javase/specs/jls/se8/html/jls-8.html#jls-8.1.5
			referent.implements = [];

			for (const superinterface of decl.implementsClause) {
				let resolution = resolveTypeTree(scope, superinterface, ctx);
				if (!resolution) return;

				// "Each InterfaceType must name an accessible interface type (§6.6), or a compile-time error occurs."
				if (maybeUnparameterize(resolution)?.type !== "interface")
					return reportError(
						new CompileError("Interface expected", superinterface.span)
					);

				resolution = resolution as PermittedSuperclassType;

				// "If an InterfaceType has type arguments, it must denote a well-formed parameterized type (§4.5),
				// and none of the type arguments may be wildcard type arguments, or a compile-time error occurs."
				const wildcardError = anyWildcardError(resolution);
				if (wildcardError)
					return reportError(
						new CompileError("Wildcards not allowed here", superinterface.span)
					);

				// "It is a compile-time error if a class depends on itself."
				const circularityCheck = classCircularityCheck(referent, resolution);
				if (circularityCheck)
					return reportError(
						new CompileError(circularityCheck, superinterface.span)
					);

				referent.implements.push(resolution);
			}
		}
	}

	private resolveInterfaceDeclarationRelations(decl: InterfaceDeclarationTree) {
		const referent = decl.referent!;
		const scope = decl.scope!.prev!;

		referent.extends = [];

		const ctx = this.makeAnalysisContext(referent.file);

		if (decl.extendsClause) {
			// https://docs.oracle.com/javase/specs/jls/se8/html/jls-9.html#jls-9.1.3

			for (const superinterface of decl.extendsClause) {
				let resolution = resolveTypeTree(scope, superinterface, ctx);

				// "Each InterfaceType in the extends clause of an interface declaration must name an accessible
				// interface type (§6.6), or a compile-time error occurs.
				if (!resolution) return;
				const unparameterized = maybeUnparameterize(resolution);
				if (unparameterized?.type !== "interface")
					return reportError(
						new CompileError(`Interface expected`, superinterface.span)
					);

				// "If an InterfaceType has type arguments, it must denote a well-formed parameterized type (§4.5),
				// and none of the type arguments may be wildcard type arguments, or a compile-time error occurs."
				const wildcardError = anyWildcardError(resolution);
				if (wildcardError)
					return reportError(
						new CompileError("Wildcards not allowed here", superinterface.span)
					);

				resolution = resolution as PermittedSuperclassType;

				// "It is a compile-time error if an interface depends on itself."
				const circularityCheck = classCircularityCheck(referent, resolution);
				if (circularityCheck)
					return reportError(
						new CompileError(circularityCheck, superinterface.span)
					);

				referent.extends.push(resolution);
			}
		}
	}

	/**
	 * For use only by the JDK generator, to remove types unlikely to be used by students!
	 */
	_forceDeleteClassByBinaryName(clazz: string) {
		const info = this.getClassByBinaryName(clazz)!;
		if (!info) return null;

		if (info.enclosedBy === null)
			info.package?.topLevelClasses.delete(info.simpleName);
		else info.enclosedBy.memberClasses.delete(info.simpleName);
	}

	*listFiles(): Generator<FileInfo> {
		for (const v of this.files.values()) {
			yield v;
		}
	}

	/**
	 * Given a string representing a type, create a new instance of that type as if it appeared in a declaration
	 * in the root package. Returns null on failure.
	 * @param s Description of the type, e.g. ArrayList<Integer>
	 * @param ctx Analysis context
	 */
	type(s: string, ctx: Partial<AnalysisCtx> = {}): GeneralType | null {
		const parsed = new Parser(s).consumePrimitiveOrReferenceType();
		if (parsed === null) return null;
		return resolveTypeTree(
			this.rootScope,
			parsed,
			fillContext(ctx, this.javaVersion())
		) as GeneralType;
	}

	/**
	 * Given a string representing an expression, parse and resolve the expression as if it appeared in the root
	 * package. Returns null on failure.
	 * @param expr The expression, e.g. 3 + 2
	 * @param ctx Analysis context
	 */
	expression(
		expr: string,
		ctx: Partial<AnalysisCtx> = {}
	): BaseExpressionTree | null {
		const parsed = new Parser(expr).consumeExpression();
		if (parsed === null) return null;
		resolveExpressionType(
			this.rootScope,
			parsed,
			fillContext(ctx, this.javaVersion())
		);
		return parsed;
	}
}
